import { mistToDgc } from './amount.js';
import { formatEvmAddressFromBytes } from './buildBridge.js';
import { normalizeToHexAddress } from './cpcAddress.js';
function parseRecipientHex(raw) {
    if (Array.isArray(raw)) {
        const bytes = Uint8Array.from(raw.map((n) => Number(n) & 0xff));
        if (bytes.length === 20)
            return formatEvmAddressFromBytes(bytes);
        return `0x${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
    }
    if (typeof raw === 'string')
        return raw;
    return '—';
}
function parseLockedRow(ev) {
    const body = ev.parsedJson;
    if (!body)
        return null;
    const amountMist = String(body.amount ?? '0');
    return {
        nonce: String(body.nonce ?? ''),
        sender: String(body.sender ?? ''),
        amountMist,
        amountCpc: mistToDgc(BigInt(amountMist)),
        targetChainId: String(body.target_chain_id ?? body.targetChainId ?? ''),
        recipientHex: parseRecipientHex(body.recipient),
        digest: ev.id.txDigest,
        timestampMs: ev.timestampMs ? Number(ev.timestampMs) : null,
    };
}
function sameAccount(a, b) {
    try {
        return normalizeToHexAddress(a) === normalizeToHexAddress(b);
    }
    catch {
        return a.trim().toLowerCase() === b.trim().toLowerCase();
    }
}
export async function fetchBridgeLockedEvents(client, packageId, opts) {
    const limit = opts?.limit ?? 20;
    const filter = { MoveEventType: `${packageId}::bridge::Locked` };
    const chainFilter = opts?.chainId != null ? BigInt(opts.chainId) : null;
    const senderFilter = opts?.sender?.trim() ? opts.sender.trim() : null;
    const rows = [];
    let cursor = null;
    const maxPages = senderFilter ? 12 : 3;
    for (let page = 0; page < maxPages && rows.length < limit; page++) {
        const res = await client.queryEvents({
            query: filter,
            limit: 50,
            order: 'descending',
            cursor,
        });
        for (const ev of res.data) {
            const row = parseLockedRow(ev);
            if (!row)
                continue;
            if (chainFilter != null && BigInt(row.targetChainId || '0') !== chainFilter)
                continue;
            if (senderFilter && !sameAccount(row.sender, senderFilter))
                continue;
            rows.push(row);
            if (rows.length >= limit)
                break;
        }
        if (!res.hasNextPage || !res.nextCursor)
            break;
        cursor = res.nextCursor;
    }
    return rows;
}
export async function verifyBridgeObjectsReachable(client, config) {
    for (const id of [config.packageId, config.bridgeId]) {
        const r = await client.getObject({ id, options: { showType: true } });
        if (!r.data?.objectId || r.error)
            return false;
    }
    const bridge = await client.getObject({ id: config.bridgeId, options: { showContent: true } });
    const content = bridge.data?.content;
    if (!content?.fields)
        return false;
    return true;
}
export async function fetchBridgeTreasuryMist(client, bridgeId) {
    const r = await client.getObject({ id: bridgeId, options: { showContent: true } });
    const content = r.data?.content;
    const treasury = content?.fields?.treasury;
    if (treasury == null)
        return null;
    if (typeof treasury === 'string' || typeof treasury === 'number')
        return BigInt(treasury);
    const nested = treasury;
    if (nested.fields?.value != null)
        return BigInt(nested.fields.value);
    return null;
}
